import * as React from 'react';
import {Stack, Text, VisuallyHidden} from '@treatwell/ui';
import {shallowEqual} from 'react-redux';
import styles from './DayItem.module.css';
import clsx from 'clsx';
import {Day} from '../DayList/DayList';

type Props = {
  onSelected?: (readableDate: string) => void;
  isSelected?: boolean;
  dayRef?: (node: HTMLButtonElement) => void;
  isSmallViewport: boolean;
} & Day &
  Pick<React.JSX.IntrinsicElements['button'], 'tabIndex'>;

export const DayItem = React.memo(
  ({
    dayName,
    dayNumber,
    isSelected,
    onSelected,
    isAvailable,
    date,
    dayRef,
    tabIndex,
    isSmallViewport,
  }: Props) => {
    const dayType = isSmallViewport ? 'mdHeader' : 'xlHeader';

    return (
      <button
        className={styles.root}
        onClick={(event) => {
          event.preventDefault();
          onSelected?.(date);
        }}
        tabIndex={tabIndex}
        type="button"
        data-cy="DayItem"
        data-selected={isSelected}
        ref={dayRef}
      >
        <Stack as="dl" space="sm">
          <Text
            aria-hidden
            className={clsx({
              [styles.unAvailable]: !isAvailable,
            })}
            as="dt"
            type="caption"
          >
            {dayName}
          </Text>
          <Text
            aria-hidden
            className={clsx(styles.date, {
              [styles.selected]: isSelected,
              [styles.unAvailable]: !isAvailable,
            })}
            as="dd"
            type={dayType}
          >
            {dayNumber}
          </Text>
          <VisuallyHidden as="time">{date}</VisuallyHidden>
        </Stack>
      </button>
    );
  },
  propsComparison,
);

// Skip checking the `dayRef` function as that is created fresh each time in a
// loop.
function propsComparison(prevProps: Props, nextProps: Props) {
  const prevPropsToCompare = omitProps(prevProps);
  const nextPropsToCompare = omitProps(nextProps);
  return shallowEqual(prevPropsToCompare, nextPropsToCompare);
}

function omitProps(props: Props) {
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const {dayRef, ...rest} = props;
  return rest;
}

DayItem.displayName = 'DayItem';
